import React from 'react';
import { useMdxMedia } from '~contexts/MdxMediaContext';

// Import all MDX components
import Note from '~components/Note';
import Image from '~components/MdxImage';
import Video from '~components/MdxVideo';
import Link from "~components/Link";
import { MdxLayout, MdxMedia } from '~components/MdxLayout';
import TextTip from '~components/TextTip';
import ToolTip from '~components/ToolTip';
import ArrowFlow from '~components/ArrowFlow';
import ItemBlurb from '~components/ItemBlurb';
import Gallery from '~components/Gallery';
import Button from '~components/Button';
import Citation from '~components/Citation';
import VideoEmbed from '~components/VideoEmbed';
import ButtonGroup from '~components/ButtonGroup';

// Export a function that generates components with media context
// This will be called at runtime with postMedia
export const createMdxComponents = (postMedia = { images: {}, videos: {} }) => {
  return {
    Link,
    a: Link,
    Image: function(props) {
      return <Image imgs={postMedia.images} {...props} />
    },
    Video: function(props) {
      return <Video vids={postMedia.videos} {...props} />
    },
    MdxLayout,
    MdxMedia,
    TextTip,
    ToolTip,
    ArrowFlow,
    ItemBlurb,
    Note,
    Gallery: function(props) {
      return <Gallery {...props} imgs={postMedia.images} />;
    },
    Button,
    ButtonGroup,
    VideoEmbed,
    cite: Citation
  };
};

// Export components for global scope (without media context)
// These will be used during MDX compilation
// At runtime, MDXProvider will override these with the media-aware versions
// Note: Image and Gallery will receive empty media objects during compilation,
// but this is okay as they'll be replaced at runtime by MDXProvider

// Named exports for globalScope
export const LinkComponent = Link;
export const ImageComponent = function(props) {
  return <Image imgs={{}} {...props} />
};
export const VideoComponent = function(props) {
  return <Video vids={{}} {...props} />
};
export const MdxLayoutComponent = MdxLayout;
export const MdxMediaComponent = MdxMedia;
export const TextTipComponent = TextTip;
export const ToolTipComponent = ToolTip;
export const ArrowFlowComponent = ArrowFlow;
export const ItemBlurbComponent = ItemBlurb;
export const NoteComponent = Note;
export const GalleryComponent = function(props) {
  return <Gallery {...props} imgs={{}} />;
};
export const ButtonComponent = Button;
export const ButtonGroupComponent = ButtonGroup;
export const VideoEmbedComponent = VideoEmbed;
export const CiteComponent = Citation;

// Components that pull media from context
export const ImageWithContext = (props) => {
  const { images } = useMdxMedia();
  return <Image imgs={images} {...props} />;
};

export const VideoWithContext = (props) => {
  const { videos } = useMdxMedia();
  return <Video vids={videos} {...props} />;
};

export const GalleryWithContext = (props) => {
  const { images } = useMdxMedia();
  return <Gallery {...props} imgs={images} />;
};

// Default export for runtime use - components pull media from context
const defaultComponents = {
  Link,
  a: Link,
  Image: ImageWithContext,
  Video: VideoWithContext,
  MdxLayout,
  MdxMedia,
  TextTip,
  ToolTip,
  ArrowFlow,
  ItemBlurb,
  Note,
  Gallery: GalleryWithContext,
  Button,
  ButtonGroup,
  VideoEmbed,
  cite: Citation
};

export default defaultComponents;

// Re-export commonly used components for direct imports in MDX files
export { 
  ImageWithContext as Image,
  VideoWithContext as Video, 
  GalleryWithContext as Gallery,
  MdxLayout,
  MdxMedia,
  Link,
  Note,
  TextTip,
  ToolTip,
  ArrowFlow,
  ItemBlurb,
  Button,
  ButtonGroup,
  VideoEmbed,
  Citation
};

